import { utcToZonedTime, formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

import { addSeconds, addMinutes, startOfDay, addDays } from 'date-fns';
import { isNil } from 'lodash';
import { getConfig } from '../AppConfig';
import { serializeItem, isConsumerApp } from './Helpers';
import { getBusinessTimezone } from './businessTimezone';

export const ORDER_TYPE_OPTIONS = [
  { label: 'Takeout', value: 'takeout', text: 'Pickup' },
  { label: 'Delivery', value: 'delivery', text: 'Delivery' },
];
export const WEEK_DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
export const DAY_FULL_NAMES = {
  TODAY: 'Today',
  TMR: 'Tomorrow',
  SUN: 'Sunday',
  MON: 'Monday',
  TUE: 'Tuesday',
  WED: 'Wednesday',
  THU: 'Thursday',
  FRI: 'Friday',
  SAT: 'Saturday',
};
export const ASAP_OPTION = {
  value: 'asap',
  type: 'asap',
  label: 'ASAP',
};

export const toBusinessTimezone = (date = new Date()) => {
  const timezone = getBusinessTimezone();
  const inputZoned = utcToZonedTime(date, timezone);
  return zonedTimeToUtc(inputZoned, timezone);
};

export const timeNow = (add = 0) => {
  const timezone = getBusinessTimezone();
  const today = toBusinessTimezone();
  return formatInTimeZone(addMinutes(today, add), timezone, 'p');
};

export const getDateOptions = (restaurantTimezone, now = new Date()) => {
  const timezone = getBusinessTimezone(restaurantTimezone);
  // Advance calendar days in local wall time, not in the browser timezone.
  // Noon avoids landing on a missing/repeated hour at DST boundaries.
  const localNoon = new Date(
    `${formatInTimeZone(now, timezone, 'yyyy-MM-dd')}T12:00:00`
  );

  const options = [];
  while (options.length < 6) {
    const day = addDays(localNoon, options.length);
    let label = WEEK_DAY_NAMES[day.getDay()];
    if (options.length === 0) label = 'TODAY';
    if (options.length === 1) label = 'TMR';
    options.push({
      label,
      fullName: DAY_FULL_NAMES[label],
      value: zonedTimeToUtc(day, timezone).getTime() / 1000,
      dayOfWeek: day.getDay(),
      dayOfMonth: day.getDate(),
    });
  }

  const result = options.map((i) => ({
    ...i,
    key: i.dayOfMonth,
    value: i.value,
  }));

  return result;
};

export const getIntervalWithSteps = (interval, step) => {
  if (!Number.isFinite(step) || step <= 0) return [];
  const { start, end } = interval;

  const dates = [];
  let currentTimestamp = start.getTime();

  while (currentTimestamp < end.getTime()) {
    const intervalStart = new Date(currentTimestamp);
    dates.push(intervalStart);
    currentTimestamp += step * 1000;
  }

  return dates;
};

const getTimeOptionsByWindows = (date, windows, optionsConfig) => {
  const { businessTimezone, step } = optionsConfig;

  const result = [];

  for (let j = 0; j < windows.length; j++) {
    // TODO: should read from the array now
    const item = windows[j];
    if (isNil(item?.start) || isNil(item?.end)) {
      break;
    }

    const localDay = startOfDay(utcToZonedTime(date, businessTimezone));
    const startTimeDate = zonedTimeToUtc(
      addSeconds(localDay, item.start),
      businessTimezone
    );
    const endTimeDate = zonedTimeToUtc(
      addSeconds(localDay, item.end),
      businessTimezone
    );

    const timeIntervals = { start: startTimeDate, end: endTimeDate };

    const intervals = getIntervalWithSteps(timeIntervals, step);

    intervals.forEach((interval) => {
      const intervalStartEpoch = interval.getTime() / 1000;
      const intervalEndEpoch = interval.getTime() / 1000 + step;

      const startTimeFormatted = formatInTimeZone(
        interval,
        businessTimezone,
        'h:mma'
      );
      const endIntervalTimeDate = addSeconds(interval, step);
      const endTimeFormatted = formatInTimeZone(
        endIntervalTimeDate,
        businessTimezone,
        'h:mma'
      );

      result.push({
        label: `${startTimeFormatted} - ${endTimeFormatted}`,
        value: {
          start: intervalStartEpoch,
          end: intervalEndEpoch,
          timezone: businessTimezone,
        },
        type: 'target',
      });
    });
  }

  return result;
};

export const getTimeOptions = ({
  type,
  epoch = null,
  restaurantDetails,
  now = new Date(),
}) => {
  const config = getConfig();
  if (restaurantDetails && !restaurantDetails.schedules) return [];
  let options = [];

  let dateValue = new Date();
  if (epoch) {
    dateValue = new Date(epoch * 1000);
  }

  const businessTimezone = getBusinessTimezone(restaurantDetails?.timezone);
  const businessTakeoutDelay = isConsumerApp()
    ? config?.consumerSetup?.delays?.takeout
    : 0;
  const businessDeliveryDelay = isConsumerApp()
    ? config?.consumerSetup?.delays?.delivery
    : 0;
  const takeoutWindows = isConsumerApp()
    ? config?.consumerSetup?.windows?.takeout
    : [];
  const deliveryWindows = isConsumerApp()
    ? config?.consumerSetup?.windows?.delivery
    : [];
  const takeoutIntervalSize = isConsumerApp()
    ? config?.consumerSetup?.intervals?.takeout
    : 300;
  const deliveryIntervalSize = isConsumerApp()
    ? config?.consumerSetup?.intervals?.delivery
    : 900;

  const nowDay = formatInTimeZone(now, businessTimezone, 'yyyy-MM-dd');
  const selectedDay = formatInTimeZone(
    dateValue,
    businessTimezone,
    'yyyy-MM-dd'
  );
  const dayIndex =
    (parseInt(formatInTimeZone(dateValue, businessTimezone, 'i'), 10) - 1) % 7;

  let windows = null;
  let delay = null;
  let step = null;
  if (type === 'delivery') {
    step = deliveryIntervalSize;
  } else if (type === 'takeout') {
    step = takeoutIntervalSize;
  }

  if (restaurantDetails?.schedules) {
    const schedule = restaurantDetails.schedules[type];
    // Details can arrive before their schedule. Do not substitute the generic
    // business windows for a restaurant with an incomplete schedule.
    if (!schedule?.[dayIndex]) return [];
    if (!Array.isArray(schedule[dayIndex].openings)) return [];
    windows = schedule[dayIndex].openings.map(({ open, close }) => ({
      start: open,
      end: close,
    }));
    if (type === 'delivery') {
      delay = restaurantDetails.delays?.delivery ?? null;
    } else if (type === 'takeout') {
      delay = restaurantDetails.delays?.takeout ?? null;
    }
  } else if (type === 'delivery') {
    windows = deliveryWindows?.[dayIndex];
    delay = businessDeliveryDelay;
  } else if (type === 'takeout') {
    windows = takeoutWindows?.[dayIndex];
    delay = businessTakeoutDelay;
  }

  if (
    !Array.isArray(windows) ||
    !Number.isFinite(delay) ||
    !Number.isFinite(step) ||
    step <= 0
  ) {
    return [];
  }

  const isToday = nowDay === selectedDay;

  const optionsConfig = {
    businessTimezone,
    step,
  };

  const limitEpochForAsap = now.getTime() / 1000 + delay;
  const timeOptions = getTimeOptionsByWindows(
    dateValue,
    windows,
    optionsConfig
  );
  let inInterval = !restaurantDetails?.schedules;

  if (isToday) {
    for (let index = 0; index < timeOptions.length; index++) {
      const timeOption = timeOptions[index];
      if (limitEpochForAsap < timeOption.value.start) {
        options.push(timeOption);
      } else if (
        limitEpochForAsap >= timeOption.value.start &&
        limitEpochForAsap < timeOption.value.end
      ) {
        inInterval = true;
      }
    }
  } else {
    options.push(...timeOptions);
  }

  if (isToday) {
    if (inInterval) {
      options = [
        {
          ...ASAP_OPTION,
          value: {
            start: Math.floor(limitEpochForAsap + 100).toString(),
            end: Math.floor(limitEpochForAsap + step + 100).toString(),
            timezone: businessTimezone,
          },
        },
        ...options,
      ];
    }
  }

  return options;
};

// A nearest-slot read needs only one day/mode. The picker still gets the full
// result by default. No slot or availability data is cached across calls.
const getAllOptions = (
  restaurantDetails,
  { dayKey, mode, todayOnly = false } = {}
) => {
  const dateOptions = getDateOptions(restaurantDetails?.timezone);

  const timeOptions = {};
  const distanceOptions = [
    { label: 'Any', value: 0 },
    { label: 'Less than 5mi', value: 5 },
    { label: 'Less than 10mi', value: 10 },
    { label: 'Less than 15mi', value: 15 },
  ];

  dateOptions.forEach((dateOption, index) => {
    if (todayOnly && index > 0) return;
    if (dayKey != null && String(dateOption.key) !== String(dayKey)) return;
    timeOptions[dateOption.key] = {
      takeout:
        mode && mode !== 'takeout'
          ? []
          : getTimeOptions({
              type: 'takeout',
              epoch: dateOption.value,
              restaurantDetails,
            }),
      delivery:
        mode && mode !== 'delivery'
          ? []
          : getTimeOptions({
              type: 'delivery',
              epoch: dateOption.value,
              restaurantDetails,
            }),
    };
  });

  const result = {
    typeOptions: ORDER_TYPE_OPTIONS,
    dateOptions,
    timeOptions,
    distanceOptions,
  };

  return serializeItem(result);
};

export const findOption = (option, list = []) => {
  for (let i = 0; i < list.length; i += 1) {
    if (JSON.stringify(list[i]) === JSON.stringify(option)) {
      return list[i];
    }
  }

  return null;
};

export default getAllOptions;
